import { stripHtmlToText } from "@salestrak/utilities";
import { z } from "zod";

import { currencyFractionDigits, fractionDigitsOf } from "./currencies.ts";
import { numberDecimalPlaces } from "./number-format.ts";
import { FIELD_TYPES } from "./field-types.ts";
import type { CustomFieldSpec } from "./spec.ts";
import { isValidPhoneValue } from "./phone-format.ts";
import { isValidUrlText } from "./url-format.ts";

/** The email FIELD's format rule: a full address, `local@domain.tld`. The
 *  domain needs at least one dot, every dot must be followed by a label
 *  (`a@d.` is not done yet), and the last label has 2+ characters, so `a@d`
 *  and `a@d.c` are refused. The live editing ring, the commit gate and the
 *  API read the SAME predicate, so a draft the ring cleared can never be
 *  refused at blur or on write. */
export function isValidEmailText(value: string): boolean {
  return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[^\s@.]{2,}$/.test(value);
}

/** Per-type length caps from the product rule sheet.
 *
 *  Two gates read this and they must agree: the UI gate (`<Input maxLength>`)
 *  and the commit gate (the schemas below). `phone` and `address` are UI-only —
 *  each stores an object, so a character cap cannot apply to the stored value. */
export const CUSTOM_FIELD_MAX_LENGTHS: Readonly<
  Record<string, number | undefined>
> = {
  text: 255,
  paragraph: 50000,
  email: 254,
  phone: 25,
  url: 2000,
  address: 500,
};

const optionalText = z.string().nullish();

const capped = (cap: number | undefined) =>
  cap === undefined
    ? z.string()
    : z.string().max(cap, { error: `Max ${cap} characters` });

/** A number as the form layer produces it: already a number, a numeric string,
 *  or empty. Anything unparseable becomes `null` — "unanswered" — never `NaN`,
 *  which silently defeats every comparison a min or max check makes. */
const numericValue = z.unknown().transform((raw) => {
  if (raw === undefined || raw === null) return raw;
  if (typeof raw === "number") return Number.isNaN(raw) ? null : raw;
  const text = String(raw).trim();
  if (text === "") return null;
  const parsed = Number(text);
  return Number.isNaN(parsed) ? null : parsed;
});

/** An amount as the editor commits it (a number) or as a stored amount reads
 *  back through the form layer (its decimal string). Blank is unanswered. */
const moneyValue = z.unknown().transform((raw, ctx) => {
  if (raw === undefined || raw === null) return raw;
  const parsed =
    typeof raw === "number" ? raw : String(raw).trim() === "" ? null : Number(raw);
  if (parsed === null) return null;
  if (!Number.isFinite(parsed)) {
    ctx.addIssue({ code: "custom", message: "Not a number" });
    return z.NEVER;
  }
  return parsed;
});

/** Applies the field's `min` / `max` config to a numeric schema. Both money and
 *  number carry the pair (the form dialog writes it for either), so both read it. */
function withBounds<T extends z.ZodType<number | null | undefined>>(
  schema: T,
  config: CustomFieldSpec["config"],
): z.ZodType {
  const { min, max } = config ?? {};
  let bounded: z.ZodType<number | null | undefined> = schema;
  if (typeof min === "number") {
    bounded = bounded.refine((value) => value == null || value >= min, {
      error: `Min ${min}`,
    });
  }
  if (typeof max === "number") {
    bounded = bounded.refine((value) => value == null || value <= max, {
      error: `Max ${max}`,
    });
  }
  return bounded;
}

/** The decimal-cap message money and number share: the tooltip sits on the
 *  value, so the field's name is already implied. */
function decimalsMessage(places: number): string {
  return places === 0
    ? "Whole numbers only"
    : `Max ${places} decimal${places === 1 ? "" : "s"}`;
}

/** The schema one custom field's value must satisfy.
 *
 *  Every field type stores "unanswered" as `null` or `undefined`, so each schema
 *  is nullish on the outside and its checks apply only to a real value. */
export function customFieldSchema(field: CustomFieldSpec): z.ZodType {
  const cap = CUSTOM_FIELD_MAX_LENGTHS[field.type];

  switch (field.type) {
    case FIELD_TYPES.money: {
      // An amount is a number with at most the currency's minor-unit digits.
      // The editor commits a number; a numeric string (the form value of a
      // stored amount) is accepted too. Unlike `number`, unparseable text is
      // an error here, not "unanswered": no money editor can produce it, so
      // it marks a broken caller rather than a blank answer.
      const digits = currencyFractionDigits(field.config?.currency);
      return withBounds(
        moneyValue.refine(
          (value) => value == null || fractionDigitsOf(value) <= digits,
          { error: decimalsMessage(digits) },
        ),
        field.config,
      );
    }

    // A number that does not parse for its region is refused, like an email.
    // The refusal keeps the editor open with the typed digits, so the user
    // can correct them (the editor never clears its own draft). A cleared
    // number is unanswered, not invalid.
    case FIELD_TYPES.phone:
      return z
        .looseObject({ value: optionalText, countryCode: optionalText })
        .refine((value) => isValidPhoneValue(value), {
          error: "Invalid phone number",
        })
        .nullish();

    case FIELD_TYPES.address:
      return z
        .looseObject({
          addressLine1: optionalText,
          city: optionalText,
          state: optionalText,
          zip: optionalText,
          country: optionalText,
        })
        .nullish();

    case FIELD_TYPES.email:
      return capped(cap)
        // Clearing an email leaves "", which is unanswered, not invalid.
        .refine((value) => value === "" || isValidEmailText(value), {
          error: "Invalid email",
        })
        .nullish();

    case FIELD_TYPES.url:
      return capped(cap)
        // Clearing a URL leaves "", which is unanswered, not invalid. The
        // predicate is the one the editor's live ring reads.
        .refine((value) => value === "" || isValidUrlText(value), {
          error: "Invalid URL",
        })
        .nullish();

    case FIELD_TYPES.number: {
      // The editor rounds to the field's decimals on commit, so a value with
      // more marks a caller that skipped it.
      const places = numberDecimalPlaces(field.config);
      return withBounds(
        numericValue.refine(
          (value) => value == null || fractionDigitsOf(value) <= places,
          { error: decimalsMessage(places) },
        ),
        field.config,
      );
    }

    case FIELD_TYPES.date:
    case FIELD_TYPES.dateTime:
    case FIELD_TYPES.time:
      // The form layer hands over a Date; a stored value arrives as an ISO
      // string. Both are legitimate input, so the schema coerces.
      return z.coerce.date().nullish();

    case FIELD_TYPES.select:
    case FIELD_TYPES.labels:
    case FIELD_TYPES.member:
      // All persist an array of ids, single-select mode included.
      return z.array(z.string()).nullish();

    case FIELD_TYPES.checkbox:
      // Tri-state: null is unanswered and is not the same as false.
      return z.boolean().nullish();

    case FIELD_TYPES.paragraph:
      return z
        .string()
        // Stored as HTML. Cap the VISIBLE text, so markup does not consume the
        // budget and a short but heavily formatted answer still commits.
        .refine(
          (value) => cap === undefined || stripHtmlToText(value).length <= cap,
          { error: `Max ${cap} characters` },
        )
        .nullish();

    case FIELD_TYPES.text:
      return capped(cap).nullish();

    default:
      // `files`, `photos` and `region` carry no character or shape constraint,
      // and `formula` is computed — no editor produces a value to validate.
      // Validation must pass for them rather than fail closed.
      return z.unknown();
  }
}

export type CustomFieldValidationResult = {
  valid: boolean;
  message: string | null;
};

/** Validates one value and returns the first problem as a display string.
 *
 *  `ZodError.issues` is the only issue list in zod 4 — the v3 `.errors` alias is
 *  gone, and `.message` holds a JSON dump of the issues, not a sentence. */
export function validateCustomFieldValue(
  field: CustomFieldSpec,
  value: unknown,
): CustomFieldValidationResult {
  const result = customFieldSchema(field).safeParse(value);
  if (result.success) return { valid: true, message: null };
  return {
    valid: false,
    message: result.error.issues[0]?.message ?? "Invalid value",
  };
}
